'use client';

import React, { useEffect, useState } from 'react';
import { EmblaOptionsType } from 'embla-carousel';
import { useLocale } from 'next-intl';

import OurNewsEmblaCarousel from '@/shared/components/Carousels/OurNewsEmblaCarousel';
import { studioClient } from '@/sanity/lib/client';
import { newsSectionQuery } from '@/sanity/queries/newsSection';
import { NewsSection } from '@/types/newsSection';
import { LoadingSpinner } from '@/shared';

export default function OurNews() {
  const OPTIONS: EmblaOptionsType = { dragFree: true, loop: true };
  const locale = useLocale();
  const [newsSection, setNewsSection] = useState<NewsSection | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsSection = async () => {
      try {
        const data = await studioClient.fetch<NewsSection>(newsSectionQuery(locale));
        setNewsSection(data);
      } catch (error) {
        console.error('Error fetching news section:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsSection();
  }, [locale]);

  if (loading) {
    return (
      <div dir="ltr" className="relative overflow-hidden">
        {/* First Layer - Radial Gradient Background */}
        <div
          className="absolute bottom-1/2 h-full w-full"
          style={{
            background: 'radial-gradient(59.43% 50% at 50% 50%, #9BFDA2 25%, #00B804 50%)',
            filter: 'blur(160px)',
            borderRadius: '1240px',
          }}
        />

        {/* Second Layer - Linear Gradient Overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(3, 1, 21, 0.72) 0%, rgba(3, 1, 21, 0.00) 100%)',
          }}
        />

        <div className="relative z-10 flex min-h-[400px] items-center justify-center pt-20">
          <div className="flex flex-col items-center space-y-4">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div dir="ltr" className="relative overflow-hidden">
      {/* First Layer - Radial Gradient Background */}
      <div
        className="absolute bottom-1/2 h-full w-full"
        style={{
          background: 'radial-gradient(59.43% 50% at 50% 50%, #9BFDA2 25%, #00B804 50%)',
          filter: 'blur(160px)',
          borderRadius: '1240px',
        }}
      />

      {/* Second Layer - Linear Gradient Overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(180deg, rgba(3, 1, 21, 0.72) 0%, rgba(3, 1, 21, 0.00) 100%)',
        }}
      />

      <div className="relative z-10 pt-20 text-center">
        <h1 className="text-hero-gradient-text text-5xl font-bold">{newsSection?.title}</h1>
        <p className="mx-auto max-w-2xl pt-4 text-center text-2xl text-white">
          {newsSection?.description}
        </p>
        {/* embla carousel */}
        <div className="mt-12">
          <OurNewsEmblaCarousel items={newsSection?.newsItems || []} options={OPTIONS} />
        </div>
      </div>
    </div>
  );
}
